import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, ChevronRight, ArrowRight, Wrench, Sparkles, ShieldCheck, Cpu, Package, Truck, Settings } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const ICONS = { Wrench, Sparkles, ShieldCheck, Cpu, Package, Truck, Settings };
const SITE = "https://simserv.ro";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    window.scrollTo(0, 0);
    api.get(`/services/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="bg-white min-h-screen flex flex-col">
        <Navbar company={{}} />
        <main className="flex-1 max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="font-display text-3xl font-bold text-[#0F1E36]">Serviciu inexistent</h1>
          <p className="text-slate-500 mt-3">Pagina căutată nu există.</p>
          <Link to="/servicii" className="inline-flex items-center gap-1.5 mt-6 text-[#FF5A1F] font-semibold">
            Vezi toate serviciile <ArrowRight className="w-4 h-4" />
          </Link>
        </main>
        <Footer company={{}} />
      </div>
    );

  if (!data)
    return <div className="min-h-screen flex items-center justify-center text-[#0F1E36]">Se încarcă...</div>;

  const { service: s, related, company } = data;
  const Icon = ICONS[s.icon] || Wrench;
  const phone = (company.phone || "").replace(/\s/g, "");
  const title = s.seo_title || `${s.title} în Slobozia | SIMSERV Auto Service`;
  const desc = s.seo_description || s.description || "";
  const url = `${SITE}/servicii/${s.slug || s.id}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: s.title,
    serviceType: s.title,
    description: desc,
    url,
    areaServed: { "@type": "City", name: "Slobozia" },
    provider: {
      "@type": "AutoRepair",
      name: company.name || "SIMSERV Auto Service",
      telephone: company.phone || "",
      address: {
        "@type": "PostalAddress",
        streetAddress: company.address || "",
        addressLocality: "Slobozia",
        addressRegion: "Ialomița",
        addressCountry: "RO",
      },
    },
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Acasă", item: SITE + "/" },
      { "@type": "ListItem", position: 2, name: "Servicii", item: SITE + "/servicii" },
      { "@type": "ListItem", position: 3, name: s.title, item: url },
    ],
  };

  return (
    <div className="bg-white">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        {s.image_url && <meta property="og:image" content={resolveImg(s.image_url)} />}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
        {s.faq?.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: s.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            })}
          </script>
        )}
      </Helmet>
      <Navbar company={company} />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-6 flex-wrap" data-testid="service-breadcrumbs">
          <Link to="/" className="hover:text-[#FF5A1F]">Acasă</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/servicii" className="hover:text-[#FF5A1F]">Servicii</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#0F1E36] font-medium">{s.title}</span>
        </nav>

        <div className="flex items-center gap-3 mb-4">
          <span className="w-12 h-12 rounded-xl bg-[#0F1E36] flex items-center justify-center shrink-0">
            <Icon className="w-6 h-6 text-white" />
          </span>
          {s.badge && (
            <span className="text-[11px] font-bold uppercase tracking-wide text-[#E11D2A] bg-[#E11D2A]/10 px-2.5 py-1 rounded-full">
              {s.badge}
            </span>
          )}
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0F1E36] leading-tight" data-testid="service-title">
          {s.title}
        </h1>
        {s.description && <p className="text-lg text-slate-500 mt-4">{s.description}</p>}

        {s.image_url && (
          <div className="mt-6 rounded-2xl overflow-hidden aspect-[16/9] bg-[#F4F6F9]">
            <img src={resolveImg(s.image_url)} alt={s.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div
          className="article-body mt-8 text-[#1F2937]"
          data-testid="service-content"
          dangerouslySetInnerHTML={{ __html: s.content || "" }}
        />

        {s.faq?.length > 0 && (
          <section className="mt-12" data-testid="service-faq">
            <h2 className="font-display text-2xl font-bold text-[#0F1E36] mb-5">Întrebări frecvente</h2>
            <div className="space-y-3">
              {s.faq.map((f, i) => (
                <div key={i} className="border border-[#E2E8F0] rounded-xl p-5" data-testid={`faq-item-${i}`}>
                  <h3 className="font-semibold text-[#0F1E36]">{f.q}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed text-sm">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 rounded-2xl bg-[#0F1E36] text-white p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-bold">Programează-te la SIMSERV Slobozia</h2>
            <p className="text-white/70 text-sm mt-1">Echipă specializată și echipamente moderne. Te ajutăm rapid și corect.</p>
          </div>
          {phone && (
            <a
              href={`tel:${phone}`}
              data-testid="service-call-button"
              className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04B14] text-white font-semibold px-5 py-3 rounded-lg transition-colors duration-200 shrink-0"
            >
              <Phone className="w-4 h-4" /> {company.phone}
            </a>
          )}
        </div>

        {related?.length > 0 && (
          <div className="mt-14">
            <h2 className="font-display text-2xl font-bold text-[#0F1E36] mb-5">Alte servicii</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((r) => {
                const RI = ICONS[r.icon] || Wrench;
                return (
                  <Link
                    key={r.id}
                    to={`/servicii/${r.slug || r.id}`}
                    data-testid={`related-service-${r.slug || r.id}`}
                    className="group bg-white rounded-xl border border-[#E2E8F0] p-5 hover:border-[#FF5A1F] hover:shadow-lg transition-all duration-300"
                  >
                    <span className="w-10 h-10 rounded-lg bg-[#0F1E36] flex items-center justify-center group-hover:bg-[#FF5A1F] transition-colors duration-300 mb-3">
                      <RI className="w-5 h-5 text-white" />
                    </span>
                    <h3 className="font-semibold text-[#0F1E36] group-hover:text-[#FF5A1F] transition-colors duration-200">{r.title}</h3>
                    <span className="mt-2 inline-flex items-center gap-1 text-sm text-[#FF5A1F] font-semibold">
                      Vezi <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </main>
      <Footer company={company} />
    </div>
  );
}
